import { useEffect, useRef } from 'react';
import { useController } from '@aha-app/mvc';
import WhiteboardController from '../../controllers/WhiteboardController';

/**
 * Hook to subscribe to whiteboard controller events with automatic cleanup
 *
 * This hook manages event listener lifecycle, ensuring proper cleanup when the component unmounts.
 * It also handles callback reference updates to ensure the latest callback is always used.
 *
 * Example usage:
 *
 * ```tsx
 * useSubscription('smartObject.updated', (event) => {
 *   // Handle the event
 *   console.log(event.detail);
 * });
 * ```
 *
 * @param eventName The name of the event to subscribe to
 * @param callback The callback function to execute when the event is triggered
 */
const useSubscription = (
  eventName: string,
  callback: (event: CustomEvent) => void
) => {
  const controller = useController(WhiteboardController);
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    const wrappedCallback = (event: CustomEvent) => {
      callbackRef.current(event);
    };
    controller.addEventListener(eventName, wrappedCallback);
    return () => {
      controller.removeEventListener(eventName, wrappedCallback);
    };
  }, [eventName, controller]);
};

export default useSubscription;
